import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, BarChart3, ChevronDown, ChevronRight, Settings2 } from 'lucide-react';

import {
  getInsightsConfig,
  getInsightsOverview,
  updateInsightsConfig,
  type InsightsConfig,
  type InsightsGroupBy,
  type InsightsGroup,
  type InsightsMetric,
  type InsightsOverview,
} from '@/api/insights.api';
import { getShipedgeReportsStatus } from '@/api/shipedgeReports.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { KpiCard, WowDelta } from '@/components/insights/KpiCard';
import {
  formatMetricValue,
  STATUS_CHIP_CLASS,
  STATUS_DOT_CLASS,
  STATUS_LABEL,
  STATUS_TEXT_CLASS,
} from '@/components/insights/format';
import { Sparkline } from '@/components/insights/Sparkline';
import { TrendChart } from '@/components/insights/TrendChart';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * Insights — the generic, configurable KPI dashboard (ADR-033).
 *
 * Reads the same Metric / MetricEntry scorecard store, then rolls it up
 * into a headline view: a summary band, featured KPI cards, a
 * sortable-by-config metrics list with per-row sparklines, and optional
 * per-group roll-ups (by department or seat).
 *
 * Everyone in the org can VIEW (metrics are already readable on
 * /scorecards). Only visionary / integrator roles see the "Configure"
 * action, which picks featured metrics, the default grouping, and the
 * week window for the whole org.
 *
 * Copy is deliberately industry-neutral — "metrics", "KPIs",
 * "on track / at risk / off track" — so the dashboard resells to any
 * company regardless of what it measures.
 */

const WEEK_OPTIONS = [13, 26];
const GROUP_OPTIONS: { value: InsightsGroupBy; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'department', label: 'Department' },
  { value: 'seat', label: 'Seat' },
];
const DEFAULT_FEATURED_COUNT = 4;

export function InsightsPage() {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();

  const canManage =
    authState.status === 'authenticated' &&
    ['visionary', 'integrator'].includes(authState.me.membership.role);

  // Per-session overrides. Null → fall back to the loaded config, then
  // to hard defaults. The controls reflect config on first load but the
  // viewer can override without persisting.
  const [weeksOverride, setWeeksOverride] = useState<number | null>(null);
  const [groupByOverride, setGroupByOverride] = useState<InsightsGroupBy | null>(null);
  const [configOpen, setConfigOpen] = useState(false);

  const configQuery = useQuery({
    queryKey: ['insights', 'config'],
    queryFn: getInsightsConfig,
  });

  // Fulfillment reports are Kaerwell-specific + org-gated. Only admins can
  // reach them, and only the one allowlisted org has the surface — so we
  // fetch the flag for admins and surface a discreet cross-link only when
  // enabled. A generic tenant never sees anything fulfillment-related here.
  const fulfillmentQuery = useQuery({
    queryKey: ['shipedge-reports', 'status'],
    queryFn: getShipedgeReportsStatus,
    enabled: canManage,
    staleTime: 5 * 60 * 1000,
  });
  const showFulfillmentLink = canManage && fulfillmentQuery.data?.enabled === true;

  const effectiveWeeks = weeksOverride ?? configQuery.data?.weekWindow ?? 13;
  const effectiveGroupBy = groupByOverride ?? configQuery.data?.defaultGroupBy ?? 'none';

  const overviewQuery = useQuery({
    queryKey: ['insights', 'overview', { weeks: effectiveWeeks, groupBy: effectiveGroupBy }],
    queryFn: () => getInsightsOverview({ weeks: effectiveWeeks, groupBy: effectiveGroupBy }),
    // Wait for config so the first fetch uses the org's defaults (avoids
    // an immediate second request when the config resolves).
    enabled: !configQuery.isLoading,
  });

  const overview = overviewQuery.data;

  // Week options always include the current effective value so its
  // button stays highlighted even if config picked a non-preset window.
  const weekOptions = useMemo(() => {
    const set = new Set<number>([...WEEK_OPTIONS, effectiveWeeks]);
    return [...set].sort((a, b) => a - b);
  }, [effectiveWeeks]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <BarChart3 className="size-5 text-kw-cerulean" />
            <h1 className="typo-h1">Insights</h1>
          </div>
          <p className="mt-1 max-w-2xl text-[14px] text-kw-muted">
            A live read on your KPIs — how each metric is tracking against its goal, the recent trend, and
            where the org stands overall. Numbers flow in from your scorecards, whether entered by hand or
            synced from an integration.
          </p>
        </div>
        {canManage ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setConfigOpen(true);
            }}
            disabled={!overview}
          >
            <Settings2 className="size-4" />
            Configure
          </Button>
        ) : null}
      </div>

      {/* ── Controls ───────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-medium text-kw-label">Window</span>
          <Segmented
            options={weekOptions.map((w) => ({ value: String(w), label: `${String(w)}w` }))}
            value={String(effectiveWeeks)}
            onChange={(v) => {
              setWeeksOverride(Number(v));
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-medium text-kw-label">Group by</span>
          <Segmented
            options={GROUP_OPTIONS.map((g) => ({ value: g.value, label: g.label }))}
            value={effectiveGroupBy}
            onChange={(v) => {
              setGroupByOverride(v as InsightsGroupBy);
            }}
          />
        </div>
      </div>

      {/* ── Body: loading / error / empty / content ────────────────── */}
      {overviewQuery.isLoading || configQuery.isLoading ? (
        <Card className="p-8 text-center text-[13px] text-kw-muted">Loading insights…</Card>
      ) : overviewQuery.isError ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load insights</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {overviewQuery.error instanceof Error ? overviewQuery.error.message : 'Please try again.'}
            </p>
          </div>
        </Card>
      ) : overview?.metrics.length === 0 ? (
        <EmptyState />
      ) : overview ? (
        <>
          <SummaryBand overview={overview} />
          <FeaturedCards overview={overview} />
          {overview.groups && overview.groups.length > 0 ? <GroupRollups groups={overview.groups} /> : null}
          <MetricsList metrics={overview.metrics} />
        </>
      ) : null}

      {/* Org-gated cross-link to the fulfillment-operations reports — only
          the allowlisted org sees this; never a generic tenant. */}
      {showFulfillmentLink ? (
        <Link
          to="/admin/shipedge/customer-profitability"
          className="flex items-center justify-between gap-3 rounded-kw-md border border-kw-border bg-white px-4 py-3 text-[13px] hover:bg-kw-bg"
        >
          <span className="flex items-center gap-2 text-kw-shark">
            <BarChart3 className="size-4 text-kw-muted" />
            <span>
              <span className="font-medium">Fulfillment operations reports</span>
              <span className="ml-1 text-kw-muted">
                — customer profitability, operational mix, lot lookup
              </span>
            </span>
          </span>
          <ArrowUpRight className="size-4 shrink-0 text-kw-muted" />
        </Link>
      ) : null}

      {/* Mount only while open so each open starts from fresh, persisted
          state (Modal has no exit animation, so unmount-on-close is clean). */}
      {overview && configOpen ? (
        <ConfigureModal
          onClose={() => {
            setConfigOpen(false);
          }}
          overview={overview}
          config={configQuery.data}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['insights'] });
            setConfigOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Segmented control
// ─────────────────────────────────────────────────────────────────────────

function Segmented({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-kw-md border border-kw-border">
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => {
            onChange(o.value);
          }}
          className={`px-3 py-1.5 text-[12px] font-medium transition-colors ${
            i > 0 ? 'border-l border-kw-border' : ''
          } ${
            o.value === value
              ? 'bg-kw-cerulean text-white'
              : 'bg-white text-kw-muted hover:bg-kw-bg hover:text-kw-shark'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Summary band
// ─────────────────────────────────────────────────────────────────────────

function SummaryBand({ overview }: { overview: InsightsOverview }) {
  const { summary } = overview;
  return (
    <Card className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 lg:grid-cols-5">
      <Stat label="On-track" value={summary.onTrackPct === null ? '—' : `${String(summary.onTrackPct)}%`}>
        <span className="text-[11px] text-kw-muted">
          {summary.onTrack} of {summary.withData} with data
        </span>
      </Stat>
      <Stat label="Metrics" value={String(summary.totalMetrics)}>
        <span className="text-[11px] text-kw-muted">{summary.noData} without data</span>
      </Stat>
      <Stat label="On track" value={String(summary.onTrack)} tone="green" />
      <Stat label="At risk" value={String(summary.atRisk)} tone="yellow" />
      <Stat label="Off track" value={String(summary.offTrack)} tone="red" />
    </Card>
  );
}

function Stat({
  label,
  value,
  tone,
  children,
}: {
  label: string;
  value: string;
  tone?: 'green' | 'yellow' | 'red';
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5">
        {tone ? <span className={`inline-block size-2 rounded-full ${STATUS_DOT_CLASS[tone]}`} /> : null}
        <span className="text-[11px] font-medium uppercase tracking-wide text-kw-muted">{label}</span>
      </div>
      <span
        className={`text-[22px] font-semibold leading-none ${tone ? STATUS_TEXT_CLASS[tone] : 'text-kw-shark'}`}
      >
        {value}
      </span>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Featured KPI cards
// ─────────────────────────────────────────────────────────────────────────

function FeaturedCards({ overview }: { overview: InsightsOverview }) {
  const byId = useMemo(() => {
    const m = new Map<string, InsightsMetric>();
    for (const metric of overview.metrics) m.set(metric.id, metric);
    return m;
  }, [overview.metrics]);

  const featured = useMemo(() => {
    if (overview.featuredMetricIds.length > 0) {
      return overview.featuredMetricIds
        .map((id) => byId.get(id))
        .filter((m): m is InsightsMetric => Boolean(m));
    }
    // No explicit featured set → surface the first few metrics.
    return overview.metrics.slice(0, DEFAULT_FEATURED_COUNT);
  }, [overview.featuredMetricIds, overview.metrics, byId]);

  if (featured.length === 0) return null;

  return (
    <section>
      <h2 className="mb-2 text-[12px] font-medium uppercase tracking-wide text-kw-muted">Featured KPIs</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {featured.map((m) => (
          <KpiCard key={m.id} metric={m} />
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Group roll-ups
// ─────────────────────────────────────────────────────────────────────────

function GroupRollups({ groups }: { groups: InsightsGroup[] }) {
  return (
    <section>
      <h2 className="mb-2 text-[12px] font-medium uppercase tracking-wide text-kw-muted">Roll-up</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {groups.map((g) => (
          <Card key={g.key} className="p-4">
            <div className="flex items-baseline justify-between gap-2">
              <p className="truncate text-[13px] font-medium text-kw-shark" title={g.label}>
                {g.label}
              </p>
              <span className="text-[18px] font-semibold text-kw-shark">
                {g.onTrackPct === null ? '—' : `${String(g.onTrackPct)}%`}
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-kw-muted">on-track · {g.totalMetrics} metrics</p>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
              <span className={STATUS_TEXT_CLASS.green}>{g.onTrack} on track</span>
              <span className={STATUS_TEXT_CLASS.yellow}>{g.atRisk} at risk</span>
              <span className={STATUS_TEXT_CLASS.red}>{g.offTrack} off track</span>
              {g.noData > 0 ? <span className="text-kw-ghost">{g.noData} no data</span> : null}
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Metrics list
// ─────────────────────────────────────────────────────────────────────────

function MetricsList({ metrics }: { metrics: InsightsMetric[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <section>
      <h2 className="mb-2 text-[12px] font-medium uppercase tracking-wide text-kw-muted">
        All metrics <span className="font-normal text-kw-ghost">({metrics.length})</span>
      </h2>
      <Card className="p-0">
        <ul className="divide-y divide-kw-border">
          {metrics.map((m) => (
            <MetricRow
              key={m.id}
              metric={m}
              expanded={expanded === m.id}
              onToggle={() => {
                setExpanded((cur) => (cur === m.id ? null : m.id));
              }}
            />
          ))}
        </ul>
      </Card>
    </section>
  );
}

function MetricRow({
  metric,
  expanded,
  onToggle,
}: {
  metric: InsightsMetric;
  expanded: boolean;
  onToggle: () => void;
}) {
  const status = metric.current?.status ?? null;
  const seriesValues = metric.series.map((p) => p.value);

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-kw-bg"
      >
        <span className="text-kw-ghost">
          {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </span>

        {/* Title + owner */}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium text-kw-shark">{metric.title}</span>
          <span className="block truncate text-[11px] text-kw-muted">
            Goal {formatMetricValue(metric.goal, metric)}
            {metric.owner ? ` · ${metric.owner.name}` : ''}
            {metric.departmentName ? ` · ${metric.departmentName}` : ''}
          </span>
        </span>

        {/* Sparkline */}
        <span
          className={`hidden h-6 w-20 shrink-0 sm:block ${status ? STATUS_TEXT_CLASS[status] : 'text-kw-ghost'}`}
        >
          <Sparkline values={seriesValues} reversed height={24} aria-label={`${metric.title} trend`} />
        </span>

        {/* Current value */}
        <span className="w-24 shrink-0 text-right text-[13px] font-medium text-kw-shark">
          {metric.current ? (
            formatMetricValue(metric.current.value, metric)
          ) : (
            <span className="text-kw-ghost">—</span>
          )}
        </span>

        {/* WoW */}
        <span className="hidden w-24 shrink-0 justify-end sm:flex">
          <WowDelta deltaPct={metric.wowDeltaPct} direction={metric.direction} />
        </span>

        {/* Status chip */}
        <span className="w-20 shrink-0 text-right">
          {status ? (
            <span
              className={`inline-flex items-center rounded-kw-full px-2 py-0.5 text-[11px] font-medium ${STATUS_CHIP_CLASS[status]}`}
            >
              {STATUS_LABEL[status]}
            </span>
          ) : (
            <span className="text-[11px] text-kw-ghost">No data</span>
          )}
        </span>
      </button>

      {expanded ? (
        <div className="border-t border-kw-border-light bg-kw-bg-blue/40 px-4 py-4">
          <TrendChart metric={metric} className="max-w-xl" />
        </div>
      ) : null}
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Empty state
// ─────────────────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <Card className="flex flex-col items-center gap-3 p-10 text-center">
      <BarChart3 className="size-8 text-kw-ghost" />
      <div>
        <p className="text-[15px] font-medium text-kw-shark">No metrics yet</p>
        <p className="mt-1 text-[13px] text-kw-muted">
          Insights builds on your scorecard metrics. Add a few to see trends and roll-ups here.
        </p>
      </div>
      <Link
        to="/scorecards"
        className="inline-flex items-center rounded-kw-md border border-kw-border bg-white px-3 py-1.5 text-[13px] font-medium text-kw-shark hover:bg-kw-bg"
      >
        Go to scorecards
      </Link>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Configure modal (admin only)
// ─────────────────────────────────────────────────────────────────────────

function ConfigureModal({
  onClose,
  overview,
  config,
  onSaved,
}: {
  onClose: () => void;
  overview: InsightsOverview;
  /** The PERSISTED config — seed the "Default" fields from THIS, not from
      `overview`, whose weekWindow/groupBy echo the viewer's transient
      window/grouping selection and would otherwise silently overwrite the
      org defaults on save. featuredMetricIds match either way (both are the
      persisted set). */
  config: InsightsConfig | undefined;
  onSaved: () => void;
}) {
  const toast = useToast();
  // The parent mounts this fresh on each open (gated on configOpen), so plain
  // initializers seed correctly — no re-seed / openKey dance needed, and a
  // canceled edit can't leak into the next open.
  const [featured, setFeatured] = useState<string[]>(overview.featuredMetricIds);
  const [groupBy, setGroupBy] = useState<InsightsGroupBy>(config?.defaultGroupBy ?? 'none');
  const [weekWindow, setWeekWindow] = useState<string>(String(config?.weekWindow ?? 13));

  const mutation = useMutation({
    mutationFn: () =>
      updateInsightsConfig({
        featuredMetricIds: featured,
        defaultGroupBy: groupBy,
        weekWindow: Number(weekWindow),
      }),
    onSuccess: () => {
      toast.show('Insights configuration saved.', { variant: 'success' });
      onSaved();
    },
    onError: (err) => {
      toast.show(describeConfigError(err), { variant: 'danger' });
    },
  });

  const weekNum = Number(weekWindow);
  const weekValid = Number.isFinite(weekNum) && weekNum >= 4 && weekNum <= 26;

  function toggleFeatured(id: string): void {
    setFeatured((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!weekValid) return;
    mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title="Configure insights" size="md">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <p className="text-[13px] font-medium text-kw-label">Featured KPIs</p>
          <p className="mt-0.5 text-[12px] text-kw-muted">
            Pick which metrics headline the dashboard. Leave all unchecked to feature the first few.
          </p>
          <div className="mt-2 max-h-52 space-y-1 overflow-y-auto rounded-kw-md border border-kw-border p-2">
            {overview.metrics.map((m) => (
              <label
                key={m.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-kw-bg"
              >
                <input
                  type="checkbox"
                  checked={featured.includes(m.id)}
                  onChange={() => {
                    toggleFeatured(m.id);
                  }}
                  className="size-4 rounded border-kw-border text-kw-cerulean focus:ring-kw-cerulean/40"
                />
                <span className="truncate text-[13px] text-kw-shark">{m.title}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[13px] font-medium text-kw-label">Default grouping</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {GROUP_OPTIONS.map((g) => (
              <button
                key={g.value}
                type="button"
                aria-pressed={groupBy === g.value}
                onClick={() => {
                  setGroupBy(g.value);
                }}
                className={`rounded-kw-md border px-3 py-1.5 text-[12px] font-medium ${
                  groupBy === g.value
                    ? 'border-kw-cerulean bg-kw-cerulean text-white'
                    : 'border-kw-border bg-white text-kw-muted hover:bg-kw-bg'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>

        <Input
          label="Default week window"
          type="number"
          min={4}
          max={26}
          value={weekWindow}
          onChange={(e) => {
            setWeekWindow(e.target.value);
          }}
          hint="Between 4 and 26 weeks."
          error={weekWindow !== '' && !weekValid ? 'Must be between 4 and 26.' : undefined}
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!weekValid}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function describeConfigError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: unknown } } }).response;
    const code = response?.data?.error;
    if (code === 'metric_not_found')
      return 'One of the featured metrics no longer exists — refresh and retry.';
    if (code === 'invalid_week_window') return 'Week window must be between 4 and 26.';
  }
  return err instanceof Error ? err.message : 'Could not save configuration.';
}
